import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { PortalPage, EmptyState, Pill } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { usePortal } from "../../components/portal/usePortal";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { fmtDateTime, EMERGENCY_STATUS, URGENCY } from "../../components/portal/portalUtils";
import { Lightning, Plus, Clock } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";

function ResponseClock({ e }) {
  if (!["open", "acknowledged", "assigned"].includes(e.status) || !e.response_hours) return null;
  const due = new Date(e.created_at).getTime() + e.response_hours * 3600 * 1000;
  const left = Math.round((due - Date.now()) / 60000);
  const late = left < 0;
  return <span className={`text-[10px] font-semibold flex items-center gap-1 ${late ? "text-red-600" : "text-amber-600"}`} data-testid={`response-clock-${e.id}`}><Clock size={11} /> {late ? `Response overdue by ${Math.abs(left)}m` : left > 120 ? `Response due in ${Math.round(left / 60)}h` : `Response due in ${left}m`}</span>;
}

export default function ClientEmergenciesPage() {
  const { perms } = usePortal();
  const teamLabel = useTeamLabel();
  const [view, setView] = useState("active");
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setItems(null);
    API.get(`/client-hub/emergencies?view=${view}`).then(({ data }) => setItems(data.emergencies || [])).catch(() => setItems([]));
  }, [view]);
  const fam = useFamilyFilter();
  const visible = (items || []).filter(fam.matches);

  return (
    <PortalPage
      title="Emergencies"
      subtitle="Rapid-response issues. Each urgency level carries a guaranteed response window."
      testid="client-emergencies"
      actions={perms.can_request && <Button size="sm" variant="destructive" onClick={() => navigate("/portal/emergencies/new")} data-testid="new-emergency-btn"><Lightning size={14} className="mr-1" weight="fill" /> Report Emergency</Button>}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
          {[["active", "Live"], ["closed", "Resolved"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`px-3 py-1 text-xs rounded ${view === k ? "bg-card shadow font-semibold" : "text-muted-foreground"}`} data-testid={`emergencies-view-${k}`}>{l}</button>
          ))}
        </div>
        <FamilyFilter fam={fam} testid="emergencies-client-filter" />
      </div>
      {items === null ? <CardListSkeleton rows={3} showHeader={false} /> : visible.length === 0 ? (
        <EmptyState icon={Lightning} title={view === "active" ? "No live emergencies" : "No emergencies found"} text="Hopefully it stays that way. If something urgent happens, report it here and we'll respond fast." />
      ) : (
        <div className="space-y-3" data-testid="emergencies-list">
          {visible.map((e) => (
            <Link key={e.id} to={`/portal/emergencies/${e.id}`} className={`block border rounded-lg bg-card p-4 card-lift ${["open", "acknowledged", "assigned"].includes(e.status) ? "border-red-300 dark:border-red-900" : "border-border"}`} data-testid={`emergency-card-${e.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-mono text-muted-foreground">{e.reference}</span>
                    <Pill map={URGENCY} value={e.urgency} />
                    <Pill map={EMERGENCY_STATUS} value={e.status} />
                    <ForClientChip name={e.for_client} clientId={e.client_id} />
                  </div>
                  <p className="text-sm font-semibold truncate">{e.title}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{e.address}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-muted-foreground">Reported {fmtDateTime(e.created_at)}</span>
                    <ResponseClock e={e} />
                  </div>
                  {e.contractor ? <p className="text-[11px] mt-1">{teamLabel.heading}: <strong>{e.contractor}</strong></p> : e.contractor_assigned ? <p className="text-[11px] mt-1 text-emerald-700 dark:text-emerald-400" data-testid={`emg-team-${e.id}`}>{teamLabel.assigned}</p> : null}
                </div>
                <div className="md:w-[40%] shrink-0"><ProgressStepper progress={e.progress} compact /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
